import { DailyWorkflowTaskResolution } from '@recurrency/core-api-schema/dist/common/enums';
import {
  DailyWorkflowRuleInfoDTO,
  DailyWorkflowRuleInfoDTOList,
} from '@recurrency/core-api-schema/dist/dailyWorkflow/getDailyWorkflowTasks';
import { ModifiedUpdatedMinMaxDTO } from '@recurrency/core-api-schema/dist/dailyWorkflow/getDailyWorkflowTaskSummary';
import { SearchPlanningDTO } from '@recurrency/core-api-schema/dist/search/getSearchPlanning';

import { splitIdNameStr } from 'utils/formatting';

import { DailyWorkflowPageState, DailyWorkflowRuleCompletionLists, GutCheckData } from './types';

export interface RuleReviewProgress {
  submitted: string[];
  reviewed: string[];
  reviewedInSession: string[];
  allReviewedItems: string[];
  totalCount: number;
  reviewedItemsCount: number;
  submittedItemsCount: number;
  areAllItemsReviewed: boolean;
  areAllItemsSubmitted: boolean;
  remainingCount: number;
}

export interface WorkflowReviewProgress {
  totalItemsCount: number;
  reviewedItemsCount: number;
  submittedItemsCount: number;
  areAllItemsReviewed: boolean;
  areAllItemsSubmitted: boolean;
  allReviewedItems: string[];
  newItemsReviewedInSessionByRule: Record<string, string[]>;
  isDailyWorkflowTasksEmpty: boolean;
  remainingCount: number;
  items: Record<string, RuleReviewProgress>;
}

export function getProjectedInventoryChange(
  item: {
    currentMin: number;
    currentMax: number;
    unitCost: number;
    recurrencyReplenishmentMethod?: string;
  },
  newMin: number,
  newMax: number,
) {
  const { currentMin, currentMax, unitCost, recurrencyReplenishmentMethod } = item;

  // For OP/OQ items, the "max' is really OP + OQ (which is min + max)
  if (recurrencyReplenishmentMethod === 'OP/OQ') {
    const newOQMax = newMin + newMax;
    const currentOQMax = currentMin + currentMax;
    return unitCost * ((newMin + newOQMax) / 2 - (currentMin + currentOQMax) / 2);
  }

  // For Min/Max items, use the projected inventory change formula
  return unitCost * ((newMin + newMax) / 2 - (currentMin + currentMax) / 2);
}

const Z_SCORE = 0.8;

// OLD METHOD - Keep for comparison
export function calculateProjectedFillRate(recommendedMin: number, recommendedMax: number, usage: number[]) {
  const stdDev = calculateStdDeviation(usage);
  const numerator = Z_SCORE * stdDev;
  const denominator = recommendedMin + 0.5 * (recommendedMax - recommendedMin);

  // Compute raw value; We can have ±Infinity/NaN here (e.g., denominator = 0)
  const raw = 1 - numerator / denominator;

  // Replace NaN/Infinity with 0, then clamp to [0, 1]
  if (!Number.isFinite(raw) || Number.isNaN(raw)) return 0;
  if (raw < 0) return 0;
  if (raw > 1) return 1;

  return raw;
}

// NEW METHOD - Probability-based service level calculation
const DAYS_PER_MONTH = 30.4375;
const EPS = 1e-8;

// Custom implementation of the error function (erf)
export function erf(x: number): number {
  // Approximation of the error function
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;

  const sign = x >= 0 ? 1 : -1;
  x = Math.abs(x);

  const t = 1.0 / (1.0 + p * x);
  const y = 1.0 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

  return sign * y;
}

function normalCdf(x: number) {
  return 0.5 * (1 + erf(x / Math.SQRT2));
}

export function calculateProjectedServiceLevel(
  recommendedMin: number,
  recommendedMax: number,
  monthlyDemand: number,
  leadTimeDays: number,
  isOpOq: boolean,
): number {
  // Inventory proxy matches the projectedInventoryChange logic (midpoint).
  const inv = isOpOq
    ? Math.max(0, recommendedMin + recommendedMax / 2)
    : Math.max(0, (recommendedMin + recommendedMax) / 2);

  // Demand over the risk window (lead time). Poisson: Var = μ.
  const T = Math.max(0, leadTimeDays);
  const mu = Math.max(0, (monthlyDemand / DAYS_PER_MONTH) * T);

  // Degenerate: essentially no expected demand in the window.
  if (mu < EPS) return inv > 0 ? 1 : 0;

  // Normal approximation to Poisson with continuity correction at cutoff k = floor(inv)
  const sigma = Math.sqrt(mu);
  const k = Math.floor(inv);
  const z = (k + 0.5 - mu) / Math.max(EPS, sigma);
  const p = normalCdf(z);

  return Math.max(0, Math.min(1, p));
}

// NEW V2 FUNCTION - Wrapper that converts usage array to monthly demand
export function calculateProjectedFillRateV2(
  recommendedMin: number,
  recommendedMax: number,
  usage: number[],
  leadTimeDays: number,
  isOpOq = false,
) {
  // Calculate monthly demand from usage array (assuming usage is monthly data)
  const monthlyDemand = usage.reduce((sum, val) => sum + val, 0) / (usage.length || 1);

  return calculateProjectedServiceLevel(recommendedMin, recommendedMax, monthlyDemand, leadTimeDays, isOpOq);
}

export function calculateStdDeviation(values: number[]) {
  if (values.length === 0) return 0;
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

export function parseGutCheckData(rawString: string): GutCheckData {
  try {
    // 1. Remove wrapping quotes if present
    let clean = rawString.trim();
    if (clean.startsWith('"') && clean.endsWith('"')) {
      clean = clean.slice(1, -1);
    }

    // 2. Replace escaped newlines and escaped quotes
    clean = clean.replace(/\\n/g, '');
    clean = clean.replace(/\\"/g, '"');

    // 3. Handle backslash-escaped backslashes (e.g., `\\\\`)
    clean = clean.replace(/\\\\/g, '\\');
    clean = clean.replace(/```json/, '');
    clean = clean.replace(/```/, '');

    // 4. Parse the final cleaned JSON string
    return JSON.parse(clean);
  } catch (error) {
    console.error('Failed to parse JSON:', error);
    return {
      success: false,
      explanation: '',
      recommendedAction: '',
    };
  }
}

export const getResolutionCounts = (
  resolutionCounts: Partial<Record<DailyWorkflowTaskResolution, number>> | null | undefined,
) => ({
  accepted: resolutionCounts?.[DailyWorkflowTaskResolution.Accepted] ?? 0,
  overridden: resolutionCounts?.[DailyWorkflowTaskResolution.Overriden] ?? 0,
  excluded: resolutionCounts?.[DailyWorkflowTaskResolution.Excluded] ?? 0,
  flagged: resolutionCounts?.[DailyWorkflowTaskResolution.Flagged] ?? 0,
});

export function getPriorReviewedItems(dailyWorkflowTasks: DailyWorkflowRuleInfoDTO[]) {
  return dailyWorkflowTasks.reduce((acc, curr) => acc.concat(curr.reviewedItemLocations), [] as string[]);
}

export function getAllSubmittedItems(dailyWorkflowTasks: DailyWorkflowRuleInfoDTO[]) {
  return dailyWorkflowTasks.reduce((acc, curr) => acc.concat(curr.submittedItemLocations), [] as string[]);
}

export function getNewItemsReviewedInSessionByRule(resolutions: DailyWorkflowPageState['resolutions']) {
  const ruleIds = {} as Record<string, string[]>;
  Object.entries(resolutions).forEach(([itemLocation, { ruleId, resolutionData }]) => {
    if (!!resolutionData?.excludeUntil || !!resolutionData?.replenishmentMethod || !!resolutionData?.flagReason) {
      if (!(ruleId in ruleIds)) ruleIds[ruleId] = [itemLocation];
      else ruleIds[ruleId].push(itemLocation);
    }
  });
  return ruleIds;
}

export function getNewItemsReviewedInSession(newItemsReviewedInSessionByRule: DailyWorkflowRuleCompletionLists) {
  return Object.entries(newItemsReviewedInSessionByRule).reduce(
    (acc, [_, ruleData]) => acc.concat(ruleData.reviewedItemLocations),
    [] as string[],
  );
}

export function getWorkflowReviewProgress(
  dailyWorkflowTasks: DailyWorkflowRuleInfoDTOList,
  resolutions: DailyWorkflowPageState['resolutions'],
): WorkflowReviewProgress {
  const previouslyReviewedItems = getPriorReviewedItems(dailyWorkflowTasks.items);

  const newItemsReviewedInSessionByRule = getNewItemsReviewedInSessionByRule(resolutions);
  const newItemsReviewedInSession = Object.values(newItemsReviewedInSessionByRule).flat();

  const allReviewedItems = [...previouslyReviewedItems, ...newItemsReviewedInSession];
  const allSubmittedItems = getAllSubmittedItems(dailyWorkflowTasks.items);

  const submittedItemsCount = allSubmittedItems.length;
  const reviewedItemsCount = allReviewedItems.length;
  const totalItemsCount = dailyWorkflowTasks.items.reduce((acc, curr) => acc + curr.taskCount, 0);

  const isDailyWorkflowTasksEmpty = totalItemsCount === 0;
  const areAllItemsReviewed = reviewedItemsCount + submittedItemsCount === totalItemsCount;
  const areAllItemsSubmitted = submittedItemsCount === totalItemsCount;
  const remainingCount = totalItemsCount - reviewedItemsCount - submittedItemsCount;

  // Build per-rule breakdown
  const items: Record<string, RuleReviewProgress> = {};

  dailyWorkflowTasks.items.forEach((rule) => {
    const ruleReviewedInSession = newItemsReviewedInSessionByRule[rule.ruleId] || [];
    const ruleAllReviewedItems = [...rule.reviewedItemLocations, ...ruleReviewedInSession];
    const ruleReviewedItemsCount = ruleAllReviewedItems.length;
    const ruleSubmittedItemsCount = rule.submittedItemLocations.length;
    const ruleRemainingCount = rule.taskCount - ruleReviewedItemsCount - ruleSubmittedItemsCount;
    const ruleAreAllItemsReviewed = ruleReviewedItemsCount + ruleSubmittedItemsCount === rule.taskCount;
    const ruleAreAllItemsSubmitted = ruleSubmittedItemsCount === rule.taskCount;

    items[rule.ruleId] = {
      submitted: rule.submittedItemLocations,
      reviewed: rule.reviewedItemLocations,
      reviewedInSession: ruleReviewedInSession,
      allReviewedItems: ruleAllReviewedItems,
      totalCount: rule.taskCount,
      reviewedItemsCount: ruleReviewedItemsCount,
      submittedItemsCount: ruleSubmittedItemsCount,
      areAllItemsReviewed: ruleAreAllItemsReviewed,
      areAllItemsSubmitted: ruleAreAllItemsSubmitted,
      remainingCount: Math.max(0, ruleRemainingCount),
    };
  });

  return {
    totalItemsCount,
    reviewedItemsCount,
    submittedItemsCount,
    areAllItemsReviewed,
    areAllItemsSubmitted,
    allReviewedItems,
    newItemsReviewedInSessionByRule,
    isDailyWorkflowTasksEmpty,
    remainingCount,
    items,
  };
}

export function getReviewedItemsCountByRule(
  dailyWorkflowTasks: DailyWorkflowRuleInfoDTO[],
  resolutions: DailyWorkflowPageState['resolutions'],
  ruleId: string,
) {
  const rulePersistedResolutions = dailyWorkflowTasks.filter((rule) => rule.ruleId === ruleId)[0];
  const rulePreviouslyReviewedItems = rulePersistedResolutions.reviewedItemLocations.length;
  const newlyReviewedItemsCount = getNewItemsReviewedInSessionByRule(resolutions)?.[ruleId]?.length ?? 0;
  return rulePreviouslyReviewedItems + newlyReviewedItemsCount;
}

/**
 * Maps search planning items to updated min/max list
 * Used to populate the new min/max update modal (MultiUpdateReplenishmentSettingsModal)
 */
export const getUpdatedMinMaxListFromSearchPlanning = (
  searchPlanningItems: SearchPlanningDTO[],
  newMin?: number,
  newMax?: number,
): ModifiedUpdatedMinMaxDTO[] =>
  searchPlanningItems.map((item) => ({
    itemId: item.item_uid,
    locationId: splitIdNameStr(item.location).foreignId,
    newMin: newMin ?? item.recommended_min,
    newMax: newMax ?? item.recommended_max,
    currentMin: item.current_min,
    currentMax: item.current_max,
    recommendedMin: item.recommended_min,
    recommendedMax: item.recommended_max,
    projectedInventoryChange: getProjectedInventoryChange(
      {
        currentMin: item.current_min,
        currentMax: item.current_max,
        unitCost: Number(item.unit_cost),
        recurrencyReplenishmentMethod: item.recurrency_replenishment_method,
      },
      newMin ?? item.recommended_min,
      newMax ?? item.recommended_max,
    ),
    itemName: item.item_name,
    itemCode: item.item_id,
    locationName: splitIdNameStr(item.location).name,
    locationDisplayName: item.location,
    replenishmentMethod: item.replenishment_method,
  }));
